import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import RichWinningResultEditor from './RichWinningResultEditor'
import useStore from '../hooks/useStore'

const MONTH_OPTIONS = Array.from({ length: 12 }, (_, index) => ({
  value: index + 1,
  label: new Date(2026, index, 1).toLocaleString('en-US', { month: 'long' })
}))

function MonthlyPanel() {
  const navigate = useNavigate()
  const {
    selectedMonthNumber,
    selectedYear,
    monthlyGoal,
    burningDesire,
    updateFormData,
    setMonthlySelection,
    loadMonthlyGoal,
    saveMonthlyGoal,
    saveBurningDesire,
    showToast
  } = useStore()

  const [bdDraft, setBdDraft] = useState(burningDesire)
  const [bdEditing, setBdEditing] = useState(!burningDesire)

  // Sync draft when DB value loads in after component mounts
  useEffect(() => {
    setBdDraft(burningDesire)
    if (burningDesire) setBdEditing(false)
  }, [burningDesire])

  const yearOptions = Array.from({ length: 7 }, (_, index) => new Date().getFullYear() - 2 + index)
  const currentMonthLabel = MONTH_OPTIONS.find((option) => option.value === selectedMonthNumber)?.label || 'Month'
  const monthlyRichKey = `monthly-${selectedYear}-${selectedMonthNumber}`

  useEffect(() => {
    loadMonthlyGoal({
      monthNumber: selectedMonthNumber,
      year: selectedYear
    })
  }, [selectedMonthNumber, selectedYear, loadMonthlyGoal])

  const handleSave = async () => {
    if (!monthlyGoal.trim()) {
      showToast('⚠', 'Set a monthly goal first')
      return
    }

    try {
      await saveMonthlyGoal({
        monthNumber: selectedMonthNumber,
        year: selectedYear
      })
      showToast('✓', 'Monthly goal saved')
    } catch (error) {
      showToast('❌', error?.message || 'Failed to save monthly goal')
    }
  }

  const handleSaveBd = async () => {
    if (!bdDraft.trim()) { showToast('⚠', 'Write your burning desire first'); return }
    try {
      await saveBurningDesire(bdDraft.trim())
      setBdEditing(false)
      showToast('✓', 'Burning desire saved')
    } catch (error) {
      showToast('❌', error?.message || 'Failed to save — check connection')
    }
  }

  return (
    <div className="panel active">
      <div className="pg-ey">Step 00 · Monthly Direction</div>
      <div className="pg-ti">Define the <em>Monthly Result Area</em></div>
      <div className="pg-su">
        Choose the month you want to plan, then define the one concrete result that month must produce.
      </div>

      {/* ── Burning Desire ── */}
      <div className="bd-card mb24">
        <div className="bd-header">
          <div className="bd-label-row">
            <svg className="bd-flame" viewBox="0 0 12 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
              <path d="M6 15C6 15 1 11.5 1 7C1 4.5 3 2.5 4.5 1.5C4.5 3.5 5.5 4.5 6.5 4C6 5.5 7 7 8.5 6C8.5 8 7.5 9.5 9 10.5C9.5 8.5 10.5 7.5 11 6.5C11.5 8 11 10.5 10 12C10 12 11 11 11 9.5C11.5 10.5 11 12.5 10 13.5C9 14.5 7.5 15 6 15Z" fill="var(--primary)" opacity="0.9"/>
            </svg>
            <span className="bd-label">Burning Desire</span>
          </div>
          <span className="bd-subtitle">The reason you show up on day 47 when novelty is gone</span>
        </div>

        {bdEditing ? (
          <div className="bd-edit-area">
            <textarea
              className="bd-textarea"
              rows="3"
              placeholder="What do I want so badly that I'll endure the discomfort of honest execution every single day?"
              value={bdDraft}
              onChange={(e) => setBdDraft(e.target.value)}
              onInput={(e) => { e.target.style.height = 'auto'; e.target.style.height = `${e.target.scrollHeight}px` }}
              autoFocus={!!burningDesire}
            />
            <div className="bd-actions">
              <button className="btn bd-save-btn" onClick={handleSaveBd}>Save</button>
              {burningDesire && (
                <button className="btn bd-cancel-btn" onClick={() => { setBdDraft(burningDesire); setBdEditing(false) }}>Cancel</button>
              )}
            </div>
          </div>
        ) : (
          <div className="bd-display">
            <p className="bd-display-text">{burningDesire}</p>
            <button
              className="bd-edit-btn"
              onClick={() => { setBdDraft(burningDesire); setBdEditing(true) }}
              title="Edit burning desire"
              aria-label="Edit burning desire"
            >
              <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round">
                <path d="M9.5 2.5l2 2L4 12H2v-2L9.5 2.5z"/>
              </svg>
            </button>
          </div>
        )}
      </div>

      <div className="card mb24">
        <div className="cg gold"></div>
        <div className="c-lbl">Monthly Results Area</div>
        <div className="flex gap16 ai-center mb20">
          <div className="var-badge gold">M</div>
          <div>
            <div style={{fontSize: '11px', color: 'var(--text3)', fontFamily: 'var(--ff-m)', letterSpacing: '0.18em', textTransform: 'uppercase', marginBottom: '4px'}}>Planning Horizon</div>
            <div style={{fontSize: '13px', color: 'var(--text2)', lineHeight: '1.55'}}>Lock the month first so daily goals can serve a larger outcome.</div>
          </div>
        </div>

        <div className="g2 mb20">
          <div>
            <label className="a-lbl" htmlFor="monthly-month">Month</label>
            <select
              id="monthly-month"
              className="sel"
              value={selectedMonthNumber}
              onChange={(e) => setMonthlySelection({ monthNumber: parseInt(e.target.value, 10) })}
            >
              {MONTH_OPTIONS.map((option) => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="a-lbl" htmlFor="monthly-year">Year</label>
            <select
              id="monthly-year"
              className="sel"
              value={selectedYear}
              onChange={(e) => setMonthlySelection({ year: parseInt(e.target.value, 10) })}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>{year}</option>
              ))}
            </select>
          </div>
        </div>

        <RichWinningResultEditor
          storageKey={monthlyRichKey}
          placeholder={`e.g. Close 3 qualified clients in ${currentMonthLabel} ${selectedYear}.`}
          value={monthlyGoal}
          onChange={(text) => updateFormData({ monthlyGoal: text })}
          minHeight={150}
        />
      </div>

      <div className="insight">
        <div className="iq">
          A strong month gives your daily work a frame. If the month is fuzzy, the days drift.
        </div>
      </div>

      <div className="flex gap12 mt24">
        <button className="btn bp" onClick={handleSave}>
          ✓ &nbsp;Save Monthly Goal
        </button>
        <button className="btn bg-gold" onClick={() => navigate('/morning')}>
          Morning Setup →
        </button>
      </div>
    </div>
  )
}

export default MonthlyPanel
